'use client';

import { useContext } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import type { OffersDataType } from 'components/ring-air/OffersCarousel';
import { PrimaryOffersModalData } from 'components/ring-air/ProductSelectionSection/PrimaryOffers';
import { ImageModal } from 'components/ring-pro/ProductSelectionSection/ImageModal';
import styles from './SplitPaymentsRow.module.css';

type Tone = 'light' | 'dark';

/**
 * The "Pay using HSA / FSA with Flex · See all" row under the Ring PRO price.
 * The buy page leaves `tone` unset so the sheet stays the light card.
 * The Eudemonia booth passes its light or dark theme.
 */
export const SplitPaymentsRow = ({
  data,
  tone,
}: {
  data: OffersDataType[];
  tone?: Tone;
}) => {
  const tCommon = useTranslations('common');
  const offersModal = useContext(OffersModalNewContext);
  const display = data.find((offer) => offer.displayValue)?.displayValue;
  if (!display) return null;

  const seeAll = () => {
    offersModal.set(
      <ImageModal tone={tone}>
        <PrimaryOffersModalData data={data} tone={tone} />
      </ImageModal>,
    );
    offersModal.show();
  };

  return (
    <div
      className={[styles.row, tone === 'dark' ? styles.dark : '']
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.content}>
        <div className={styles.icons} aria-hidden="true">
          {display.icons.map((icon, index) => (
            <CustomImage
              key={`${icon}-${index}`}
              src={icon}
              alt=""
              width="24"
              height="24"
            />
          ))}
        </div>
        <span className={styles.text}>{display.text}</span>
      </div>
      <button type="button" className={styles.seeAll} onClick={seeAll}>
        {tCommon('productSelectionSection.button.seeAll')}
      </button>
    </div>
  );
};
